import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Spinner, Badge } from 'react-bootstrap';
import { BsDownload } from 'react-icons/bs';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import api from '../../../api';

interface StatusCandidatosModalProps {
  show: boolean;
  processo: any;
  onClose: () => void;
}

const StatusCandidatosModal: React.FC<StatusCandidatosModalProps> = ({
  show,
  processo,
  onClose
}) => {
  const [editais, setEditais] = useState<any[]>([]);
  const [editaisSelecionados, setEditaisSelecionados] = useState<string[]>([]);
  const [operacao, setOperacao] = useState<'OR' | 'AND'>('OR');
  const [candidatos, setCandidatos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEditais, setLoadingEditais] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [allCandidatos, setAllCandidatos] = useState<any[]>([]);

  useEffect(() => {
    if (show && processo?.editais) {
      setEditais(processo.editais);
      setLoadingEditais(false);
    }
  }, [show, processo]);

  const handleToggleEdital = (editalId: string) => {
    setEditaisSelecionados(prev =>
      prev.includes(editalId)
        ? prev.filter(id => id !== editalId)
        : [...prev, editalId]
    );
  };

  const handleBuscar = async (pageNum = 0) => {
    if (editaisSelecionados.length === 0) {
      toast.warning('Selecione pelo menos um edital');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      editaisSelecionados.forEach(id => params.append('editalIds', id));
      params.append('operacao', operacao);
      params.append('page', pageNum.toString());
      params.append('size', '20');

      const response = await api.get(`/status-candidatos?${params.toString()}`);
      setCandidatos(response.data.content);
      setTotalPages(response.data.totalPages);
      setPage(pageNum);
      
      if (pageNum === 0) {
        setAllCandidatos(response.data.content);
      }
    } catch (error) {
      toast.error(BackendErrorMessage(error));
      setCandidatos([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: any = {
      'VALIDADO': 'success',
      'NAO_VALIDADO': 'danger',
      'AUSENTE': 'warning',
      'NAO_AVALIADO': 'secondary',
      'INCONCLUSIVO': 'info',
      'VALIDADO_ANTERIORMENTE': 'primary'
    };
    return statusMap[status] || 'secondary';
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleDownloadJSON = async () => {
    if (editaisSelecionados.length === 0) {
      toast.warning('Selecione pelo menos um edital');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      editaisSelecionados.forEach(id => params.append('editalIds', id));
      params.append('operacao', operacao);
      params.append('page', '0');
      params.append('size', '10000');

      const response = await api.get(`/status-candidatos?${params.toString()}`);
      const dataStr = JSON.stringify(response.data.content, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const processoNome = processo?.nome?.replace(/[^a-zA-Z0-9]/g, '-') || 'processo';
      link.download = `status-candidatos-${processoNome}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('JSON baixado com sucesso');
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Status de Candidatos por Edital</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: '20px' }}>
          <h6>Selecione os Editais:</h6>
          {loadingEditais ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {editais.map(edital => (
                <Form.Check
                  key={edital.id}
                  type="checkbox"
                  id={`edital-${edital.id}`}
                  label={edital.nome}
                  checked={editaisSelecionados.includes(edital.id)}
                  onChange={() => handleToggleEdital(edital.id)}
                />
              ))}
            </div>
          )}

          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Filtro de busca:</Form.Label>
            <Form.Select
              value={operacao}
              onChange={(e) => setOperacao(e.target.value as 'OR' | 'AND')}
            >
              <option value="OR">Candidatos inscritos em pelo menos um dos editais selecionados</option>
              <option value="AND">Candidatos inscritos em todos os editais selecionados</option>
            </Form.Select>
          </Form.Group>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="primary"
              onClick={() => handleBuscar(0)}
              disabled={loading || editaisSelecionados.length === 0}
            >
              {loading ? <Spinner animation="border" size="sm" /> : 'Buscar'}
            </Button>
            <Button
              variant="success"
              onClick={handleDownloadJSON}
              disabled={loading || editaisSelecionados.length === 0}
            >
              <BsDownload /> Baixar JSON
            </Button>
          </div>
        </div>

        {candidatos.length > 0 && (
          <>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Nome Social</th>
                  <th>Status</th>
                  <th>Inscrições</th>
                </tr>
              </thead>
              <tbody>
                {candidatos.map((candidato: any) => (
                  <tr key={candidato.candidatoId}>
                    <td>{candidato.nome}</td>
                    <td>{candidato.nomeSocial || '-'}</td>
                    <td>
                      <Badge bg={getStatusBadge(candidato.status)}>
                        {formatStatus(candidato.status)}
                      </Badge>
                    </td>
                    <td>
                      {candidato.inscricoes.map((insc: any, idx: number) => (
                        <div key={idx} style={{ fontSize: '12px' }}>
                          <strong>{insc.nomeEdital}:</strong> {insc.inscricao}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={page === 0}
                  onClick={() => handleBuscar(page - 1)}
                >
                  Anterior
                </Button>
                <span style={{ padding: '6px 12px' }}>
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={page >= totalPages - 1}
                  onClick={() => handleBuscar(page + 1)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </>
        )}

        {!loading && candidatos.length === 0 && editaisSelecionados.length > 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Nenhum candidato encontrado com os filtros selecionados.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StatusCandidatosModal;
